import { FC, useState, useEffect, useRef } from "react";
import { Modal, Form, Input, Button, notification, DatePicker, Upload, Image, Spin, Space, Divider, Radio, Descriptions } from "antd";
import { useTranslation } from "react-i18next";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ProductInfoData, ProductInfoAdd, ProductInfoAttachment, FieldSplitor } from "@/types/productInfo";
import { SaleItemState } from "@/types/sales";
import SelectSaleItemOption from "@/components/Selector/SelectSaleItemOption";
import { getFileUrl } from "@/apis/file";
import { upload } from "@/utils/file/upload";
import { addOrEdit, getInfo, attachmentAdd, attachmentDisable, attachmentUpdate } from "@/apis/productInfo";
import PrintProductInfo from "../PrintProductInfo";

// 直接使用 ProductInfoAttachment，fileTemp 字段已定义在类型中
type AttachmentValue = ProductInfoAttachment;
interface AddOrEditProps {
  id?: string;
  open: boolean;
  isDisable?: boolean;
  onClose: () => void;
  onSuccess?: (res: ProductInfoData) => void;
}

const AddOrEdit: FC<AddOrEditProps> = ({
  id,
  open,
  isDisable = false,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<ProductInfoAdd>();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedSaleItem, setSelectedSaleItem] = useState<SaleItemState | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [data, setData] = useState<ProductInfoData | undefined>(undefined);

  // 语言选项
  const languageOptions = [
    { label: t("product.languageKo"), value: 1 },
    { label: t("product.languageZh"), value: 2 },
    { label: t("product.languageEn"), value: 3 },
    { label: t("product.languageFr"), value: 4 },
    { label: t("product.languageDe"), value: 5 },
    { label: t("product.languageRu"), value: 6 },
    { label: t("product.languageAdaptive"), value: 7 },
    { label: t("product.languagePortuguese"), value: 8 },
  ];
  // channel选项
  const channelOptions = [
    { label: "1~11", value: 1 },
    { label: "1~13", value: 2 },
    { label: "1~14", value: 3 },
  ];

  //  信道选择
  const channelSelections = [
    { label: "FCC", value: 1 },
    { label: "ETSI", value: 2 },
    { label: "MKK", value: 3 },
  ];
  //  默认信道
  const defaultChannels = [
    { label: "6", value: 1 },
    { label: t("product.auto"), value: 2 },
    { label: "13", value: 3 },
    { label: t("supplierLayout.other") + "    5G:36" + `(${t("product.region36to165")})`, value: 4 },
  ];

  // 获取详情数据
  useEffect(() => {
    if (open && id) {
      setDataLoading(true);
      getInfo(id)
        .then((res) => {
          setData(res);
        })
        .catch((error) => {
          console.error("Failed to fetch product info:", error);
          notification.error({
            placement: 'top',
            message: t("counting.loadFailed"),
          });
        })
        .finally(() => {
          setDataLoading(false);
        });
    } else if (open && !id) {
      setData(undefined);
    }
  }, [open, id]);

  // 初始化表单数据
  useEffect(() => {
    if (open) {
      if (data) {
        // 编辑模式
        form.setFieldsValue({
          saleItemId: data.saleItemId,
          productId: data.productId,
          issueDate: data.issueDate ? dayjs(data.issueDate) : undefined as any,
          customerName: data.customerName,
          customerModel: data.customerModel,
          companyModel: data.companyModel,
          productionSoftware: data.productionSoftware,
          flash: data.flash,
          md5Value: data.md5Value,
          checksum: data.checksum,
          qaToolVersion: data.qaToolVersion,
          softwareVersion: data.softwareVersion,
          macAddressContent: data.macAddressContent,
          quantity: data.quantity,
          size: data.size,
          antivirusStatus: data.antivirusStatus,
          languageRequirement: data.languageRequirement ? data.languageRequirement : undefined,
          ssid: data.ssid,
          initialIp: data.initialIp,
          finishedToolVersion: data.finishedToolVersion,
          semiFinishedToolVersion: data.semiFinishedToolVersion,
          channelRange: data.channelRange,
          channelSelection: data.channelSelection,
          defaultChannel: data.defaultChannel,
          functionDescription: data.functionDescription,
          productionTestProgram: data.productionTestProgram,
          power: data.power,
          evm: data.evm,
          fer: data.fer,
          smt: data.smt,
          dip: data.dip,
          test1: data.test1,
          assy: data.assy,
          test2: data.test2,
          pack: data.pack,
          shipmentRequirements: data.shipmentRequirements,
          attachments: data.attachments || [],
        });
        // 设置销售订单数据
        if (data.saleOrderItem) {
          setSelectedSaleItem({ ...data.saleOrderItem, product: data.product || null });
        }
      } else {
        // 新增模式，清空表单
        form.resetFields();
        setSelectedSaleItem(null);
      }
    }
  }, [open, data, form]);

  // 滚动到底部
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth"
      });
    }
  };

  // 处理选择销售订单
  const handleSelectSaleItem = (item: SaleItemState) => {
    setSelectedSaleItem(item);

    // 根据选择的销售订单数据自动填充表单
    form.setFieldsValue({
      saleItemId: item.id,
      productId: item.productId,
      customerModel: item?.order?.customerModel ?? '',
      companyModel: item?.order?.mfgr ?? '',
      customerName: item?.customer?.shortName ?? item?.customer?.name1 ?? item?.customer?.name2
    });
  };

  // 显示已选择的销售订单信息 - Descriptions
  const renderSelectedSaleItemDescriptions = () => {
    if (!selectedSaleItem) return null;
    return (
      <Descriptions style={{ marginTop: -10, marginBottom: 10 }}
        column={3}
        items={[
          {
            label: t("product.orderNumber"),
            children: selectedSaleItem.order?.number || '-',
          },
          {
            label: t("sale.innerNumber"),
            children: selectedSaleItem.order?.innerNumber || '-',
          },
          {
            label: t("product.erpCode"),
            children: selectedSaleItem.product?.code || '-',
          },
          {
            label: t("product.customerName"),
            children: selectedSaleItem.customer?.shortName || '-',
          },
          {
            label: t("product.customerModel"),
            children: selectedSaleItem?.order?.customerModel || '-',
          },
          {
            label: t("product.companyModel"),
            children: selectedSaleItem?.order?.mfgr || '-',
          },
        ]}
      />
    );
  };

  // 比较字段是否发生变化（排除 attachments）
  const hasFieldsChanged = (values: ProductInfoAdd, originalData: ProductInfoData): boolean => {
    const fieldsToCompare: (keyof ProductInfoAdd & keyof ProductInfoData)[] = [
      "saleItemId",
      "productId",
      "issueDate",
      "customerName",
      "customerModel",
      "companyModel",
      "productionSoftware",
      "flash",
      "md5Value",
      "checksum",
      "qaToolVersion",
      "softwareVersion",
      "macAddressContent",
      "quantity",
      "size",
      "antivirusStatus",
      "languageRequirement",
      "ssid",
      "initialIp",
      "finishedToolVersion",
      "semiFinishedToolVersion",
      "channelRange",
      "channelSelection",
      "defaultChannel",
      "functionDescription",
      "productionTestProgram",
      "power",
      "evm",
      "fer",
      "smt",
      "dip",
      "test1",
      "assy",
      "test2",
      "pack",
      "shipmentRequirements",
    ];

    for (const field of fieldsToCompare) {
      if (values[field] !== originalData[field]) {
        if (field === "languageRequirement" && (values[field] + '') !== (originalData[field] + '')) return true
        else if (field === "issueDate" && !!values[field] && values[field]?.valueOf() !== originalData[field]) return true
        else return true;
      }
    }
    return false;
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!values.saleItemId) {
        notification.error({ placement: 'top', message: t("product.plSelectSaleOrder") });
        return;
      }

      setSubmitLoading(true);

      // 处理附件
      const attachments = values.attachments || [];
      const processedAttachments: Array<{ docId: string; description?: string }> = [];
      let hasDescriptionChanged = false;
      let hasDeletedAttachments = false;

      // 编辑模式下，检查是否有被删除的附件
      if (id && data?.attachments) {
        // const originalDocIds = data.attachments.map(a => a.docId);
        const currentDocIds = attachments
          .filter(a => a.docId && a.docId !== "temp")
          .map(a => a.docId);
        // const deletedDocIds = originalDocIds.filter(docId => !currentDocIds.includes(docId));

        const deletedIds = data.attachments.filter(atc => !currentDocIds.includes(atc.docId) && !!atc.id)
          .map(act => act.id as string)

        if (deletedIds.length > 0) {
          // 调用 attachmentDisable API 删除附件
          await attachmentDisable(deletedIds);
          hasDeletedAttachments = true;
        }
      }

      for (const attachment of attachments) {
        if (attachment.fileTemp) {
          // 新上传的文件
          try {
            const uploadedFile = await upload({
              name: attachment.fileTemp.name,
              size: attachment.fileTemp.size,
              file: attachment.fileTemp,
            });
            if (uploadedFile?.id) {
              // 编辑模式下，先调用 attachmentAdd API
              if (id) {
                await attachmentAdd(id, {
                  docId: uploadedFile.id,
                  description: attachment.description || "",
                });
              } else {
                // 新增模式下，将附件信息添加到提交数据中
                processedAttachments.push({
                  docId: uploadedFile.id,
                  description: attachment.description,
                });
              }
            }
          } catch (error) {
            console.error("Upload failed:", error);
            notification.error({
              placement: 'top',
              message: t("product.uploadFailed"),
            });
            throw error;
          }
        } else if (attachment.docId && attachment.docId !== "temp") {
          // 已存在的附件 - 检查 description 是否变化
          if (id && data?.attachments) {
            const originalAttachment = data.attachments.find(a => a.docId === attachment.docId);
            if (originalAttachment && originalAttachment.description !== attachment.description && attachment.id) {
              // description 有变化，调用 attachmentUpdate API
              try {
                await attachmentUpdate(attachment.id, attachment.description || "");
                hasDescriptionChanged = true;
              } catch (error) {
                console.error("Update attachment description failed:", error);
                notification.error({
                  placement: 'top',
                  message: t("product.updateAttachmentDescFailed"),
                });
                throw error;
              }
            }
          }
          processedAttachments.push({
            docId: attachment.docId,
            description: attachment.description,
          });
        }
      }

      // 转换日期为时间戳
      const submitData: ProductInfoAdd = {
        ...values,
        issueDate: values.issueDate ? values.issueDate.valueOf() : undefined,
        attachments: processedAttachments,
      };

      const fieldChanged = !!data && hasFieldsChanged(submitData, data)


      // 编辑模式下，检查是否有变化
      if (id && data) {
        const hasNewFiles = attachments.some((a: AttachmentValue) => a.fileTemp);
        if (!fieldChanged && !hasNewFiles && !hasDescriptionChanged && !hasDeletedAttachments) {
          onClose();
          return;
        }
      }

      // 调用创建/更新 API
      if (!id || fieldChanged) {
        const res = await addOrEdit(submitData, id);
        onSuccess?.(res);
      }

      notification.success({
        placement: 'top',
        message: id ? t("product.editSuccess") : t("product.addSuccess"),
        description: id ? t("product.editSuccessDesc") : t("product.addSuccessDesc"),
      });
      onClose();
    } catch (error: any) {
      console.error("Form submission failed:", error);
      // 判断是否是销售单已创建生产资料的错误
      if (error === 73902) {
        const orderNumber = selectedSaleItem?.order?.number || "";
        notification.error({
          placement: 'top',
          message: t("product.addFailed"),
          description: t("product.saleOrderAlreadyHasProductInfo", { orderNumber }),
        });
      } else {
        notification.error({
          placement: 'top',
          message: id ? t("product.editFailed") : t("product.addFailed"),
        });
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  // 关闭弹窗
  const handleClose = () => {
    form.resetFields();
    setSelectedSaleItem(null);
    onClose();
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <span>{isDisable ? t("product.viewProductInfo") : id ? t("product.editProductInfo") : t("product.createProductInfo")}</span>
            {data && <PrintProductInfo data={data} disabled={dataLoading} />}
          </Space>
        }
        open={open}
        onCancel={handleClose}
        width={1200}
        footer={
          isDisable ? (
            <Button onClick={handleClose}>{t("common.close")}</Button>
          ) : (
            <>
              <Button onClick={handleClose}>{t("common.cancel")}</Button>
              <Button type="primary" onClick={handleSubmit} loading={submitLoading}>
                {t("common.confirm")}
              </Button>
            </>
          )
        }
      >
        <Spin spinning={dataLoading}>
          <div ref={scrollContainerRef} style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 16 }}>
            <Form
              form={form}
              layout="horizontal"
              labelCol={{ style: { width: "auto" } }}
              wrapperCol={{ style: { flex: 1 } }}
              labelAlign="left"
              disabled={isDisable}
            >
              {/* 隐藏字段 */}
              <Form.Item name="productId" hidden>
                <Input />
              </Form.Item>

              {/* 订单信息区域 */}
              <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 16 }}>
                {t("sale.orderInfo")}
              </div>

              {/* 选择销售订单 */}
              <Form.Item
                label={t("product.orderNumber")}
                name="saleItemId"
                rules={[{ required: true, message: t("product.plSelectSaleOrder") }]}
              >
                <SelectSaleItemOption
                  onChangeSaleItem={handleSelectSaleItem}
                  placeholder={t("product.selectSaleOrder")}
                  defaultList={selectedSaleItem ? [selectedSaleItem] : []}
                  value={selectedSaleItem?.id}
                />
              </Form.Item>
              {renderSelectedSaleItemDescriptions()}
              <Form.Item label={t("sale.innerNumber")}>
                <Input value={selectedSaleItem?.order?.innerNumber || ''} disabled />
              </Form.Item>
              <Form.Item label={t("product.erpCode")}>
                <Input value={selectedSaleItem?.product?.code || ''} disabled />
              </Form.Item>

              <Form.Item label={t("product.issueDate")} name="issueDate">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item label={t("product.customerName")} name="customerName">
                <Input.TextArea autoSize disabled />
              </Form.Item>

              <Form.Item label={t("product.customerModel")} name="customerModel">
                <Input.TextArea autoSize disabled />
              </Form.Item>

              <Form.Item label={t("product.companyModel")} name="companyModel">
                <Input.TextArea autoSize disabled />
              </Form.Item>

              <Divider style={{ marginTop: 0 }} />

              {/* 基本资料说明 */}
              <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 16 }}>
                {t("product.basicInfoDesc")}
              </div>

              <Form.Item label={t("product.productionSoftware")} name="productionSoftware">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.flash")} name="flash">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.md5Value")} name="md5Value">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label="Checksum" name="checksum">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.qaToolVersion")} name="qaToolVersion">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.softwareVersion")} name="softwareVersion">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.macAddressContent")} name="macAddressContent">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.quantity")} name="quantity">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.size")} name="size">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.antivirusStatus")} name="antivirusStatus">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.languageRequirement")} name="languageRequirement">
                <Radio.Group options={languageOptions} optionType="default" />
              </Form.Item>

              <Form.Item label="SSID" name="ssid">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.initialIp")} name="initialIp">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.testProgramFinished")} name="finishedToolVersion">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.testProgramSemiFinished")} name="semiFinishedToolVersion">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label="channel" name="channelRange">
                <Radio.Group options={channelOptions} buttonStyle="solid" />
              </Form.Item>

              <Form.Item label={t("product.channelSelection")} name="channelSelection">
                <Radio.Group options={channelSelections} buttonStyle="solid" />
              </Form.Item>

              <Form.Item label={t("product.defaultChannel")} name="defaultChannel">
                <Radio.Group options={defaultChannels} buttonStyle="solid" />
              </Form.Item>
              {/* 修改输入组8 */}
              <Form.Item label={t("product.functionDescription")}>
                <Form.Item
                  name="functionDescription"
                  style={{ marginBottom: 0, display: 'none' }}
                >
                  <input />
                </Form.Item>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  shouldUpdate={(prevValues, currentValues) => {
                    return prevValues.functionDescription !== currentValues.functionDescription;
                  }}
                >
                  {() => {
                    const value = form.getFieldValue('functionDescription') || '';
                    const values = value.split(FieldSplitor);
                    const updateFieldValue = (index: number, newValue: string) => {
                      const currentValues = form.getFieldValue('functionDescription')?.split(FieldSplitor) || ['', '', '', '', '', '', '', ''];
                      currentValues[index] = newValue;
                      form.setFieldValue('functionDescription', currentValues.join(FieldSplitor));
                    };
                    return (
                      <>
                        {/* 第一行：4个文本域 */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          {[0, 1, 2, 3].map((index) => (
                            <Input.TextArea
                              key={index}
                              value={values[index] || ''}
                              onChange={(e) => updateFieldValue(index, e.target.value)}
                              autoSize
                              style={{ flex: 1 }}
                            />
                          ))}
                        </div>
                        {/* 第二行：4个文本域 */}
                        <div style={{ display: 'flex', gap: 8 }}>
                          {[4, 5, 6, 7].map((index) => (
                            <Input.TextArea
                              key={index}
                              value={values[index] || ''}
                              onChange={(e) => updateFieldValue(index, e.target.value)}
                              autoSize
                              style={{ flex: 1 }}
                            />
                          ))}
                        </div>
                      </>
                    );
                  }}
                </Form.Item>
              </Form.Item>
              {/* 输入组4 */}
              <Form.Item label={t("product.testProgram")} name="productionTestProgram">
                <Form.Item name="productionTestProgram" style={{ marginBottom: 0, display: 'none' }}>
                  <input />
                </Form.Item>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  shouldUpdate={(prevValues, currentValues) => prevValues.productionTestProgram !== currentValues.productionTestProgram}
                >
                  {() => {
                    const value = form.getFieldValue('productionTestProgram') || '';
                    const values = value.split(FieldSplitor);
                    const updateFieldValue = (index: number, newValue: string) => {
                      const currentValues = form.getFieldValue('productionTestProgram')?.split(FieldSplitor) || ['', '', '', ''];
                      currentValues[index] = newValue;
                      form.setFieldValue('productionTestProgram', currentValues.join(FieldSplitor));
                    };
                    return (
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[0, 1, 2, 3].map((index) => (
                          <Input.TextArea
                            key={index}
                            value={values[index] || ''}
                            onChange={(e) => updateFieldValue(index, e.target.value)}
                            autoSize
                            style={{ flex: 1 }}
                          />
                        ))}
                      </div>
                    );
                  }}
                </Form.Item>
              </Form.Item>
              {/* 输入组4 */}
              <Form.Item label="POWER(dbm)" name="power">
                <Form.Item name="power" style={{ marginBottom: 0, display: 'none' }}>
                  <input />
                </Form.Item>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  shouldUpdate={(prevValues, currentValues) => prevValues.power !== currentValues.power}
                >
                  {() => {
                    const value = form.getFieldValue('power') || '';
                    const values = value.split(FieldSplitor);
                    const updateFieldValue = (index: number, newValue: string) => {
                      const currentValues = form.getFieldValue('power')?.split(FieldSplitor) || ['', '', '', ''];
                      currentValues[index] = newValue;
                      form.setFieldValue('power', currentValues.join(FieldSplitor));
                    };
                    return (
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[0, 1, 2, 3].map((index) => (
                          <Input.TextArea
                            key={index}
                            value={values[index] || ''}
                            onChange={(e) => updateFieldValue(index, e.target.value)}
                            autoSize
                            style={{ flex: 1 }}
                          />
                        ))}
                      </div>
                    );
                  }}
                </Form.Item>
              </Form.Item>
              {/* 输入组4 */}
              <Form.Item label="EVM(db)" name="evm">
                <Form.Item name="evm" style={{ marginBottom: 0, display: 'none' }}>
                  <input />
                </Form.Item>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  shouldUpdate={(prevValues, currentValues) => prevValues.evm !== currentValues.evm}
                >
                  {() => {
                    const value = form.getFieldValue('evm') || '';
                    const values = value.split(FieldSplitor);
                    const updateFieldValue = (index: number, newValue: string) => {
                      const currentValues = form.getFieldValue('evm')?.split(FieldSplitor) || ['', '', '', ''];
                      currentValues[index] = newValue;
                      form.setFieldValue('evm', currentValues.join(FieldSplitor));
                    };
                    return (
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[0, 1, 2, 3].map((index) => (
                          <Input.TextArea
                            key={index}
                            value={values[index] || ''}
                            onChange={(e) => updateFieldValue(index, e.target.value)}
                            autoSize
                            style={{ flex: 1 }}
                          />
                        ))}
                      </div>
                    );
                  }}
                </Form.Item>
              </Form.Item>
              {/* 输入组4 */}
              <Form.Item label="FER(dbm)" name="fer">
                <Form.Item name="fer" style={{ marginBottom: 0, display: 'none' }}>
                  <input />
                </Form.Item>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  shouldUpdate={(prevValues, currentValues) => prevValues.fer !== currentValues.fer}
                >
                  {() => {
                    const value = form.getFieldValue('fer') || '';
                    const values = value.split(FieldSplitor);
                    const updateFieldValue = (index: number, newValue: string) => {
                      const currentValues = form.getFieldValue('fer')?.split(FieldSplitor) || ['', '', '', ''];
                      currentValues[index] = newValue;
                      form.setFieldValue('fer', currentValues.join(FieldSplitor));
                    };
                    return (
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[0, 1, 2, 3].map((index) => (
                          <Input.TextArea
                            key={index}
                            value={values[index] || ''}
                            onChange={(e) => updateFieldValue(index, e.target.value)}
                            autoSize
                            style={{ flex: 1 }}
                          />
                        ))}
                      </div>
                    );
                  }}
                </Form.Item>
              </Form.Item>

              <Divider style={{ marginTop: 0 }} />

              {/* 基本资料说明 */}
              <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 16 }}>
                {t("product.process")}
              </div>

              <Form.Item label="SMT" name="smt">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label="DIP" name="dip">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label="TEST1" name="test1">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label="ASSY" name="assy">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label="TEST2" name="test2">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label="PACK" name="pack">
                <Input.TextArea autoSize />
              </Form.Item>

              <Form.Item label={t("product.shipmentRequirements")} name="shipmentRequirements">
                <Input.TextArea autoSize />
              </Form.Item>

              {/* 附件信息 */}
              <Form.Item label={t("product.attachments")}>
                <Form.List name="attachments">
                  {(fields, { add, remove }) => (
                    <>
                      <Button
                        type="primary"
                        onClick={() => {
                          add();
                          setTimeout(() => {
                            scrollToBottom();
                          }, 300)
                        }}
                        icon={<PlusOutlined />}
                        style={{ marginBottom: 10 }}
                      >
                        {t("product.addAttachment")}
                      </Button>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                        {fields.map(({ key, name }) => (
                          <div key={key} style={{ width: "calc(50% - 8px)", position: "relative" }}>
                            {/* 删除按钮 - 右上角悬浮显示 */}
                            <Button
                              danger
                              type="text"
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                              style={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                zIndex: 10,
                                opacity: 0,
                                transition: "opacity 0.2s",
                              }}
                              className="attachment-delete-btn"
                            />
                            {/* 图片区域 */}
                            <div style={{
                              width: "100%",
                              height: 200,
                              border: "1 dashed #d9d9d9",
                              borderRadius: 4,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#fafafa",
                              marginBottom: 8,
                              overflow: "hidden"
                            }}>
                              <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => {
                                  const prev = prevValues.attachments?.[name];
                                  const curr = currentValues.attachments?.[name];
                                  return prev?.docId !== curr?.docId || prev?.fileTemp !== curr?.fileTemp;
                                }}
                              >
                                {() => {
                                  const attachments = form.getFieldValue("attachments") || [];
                                  const attachment = attachments[name];
                                  const hasImage = attachment?.fileTemp || (attachment?.docId && attachment?.docId !== "");
                                  const imageUrl = attachment?.fileTemp
                                    ? URL.createObjectURL(attachment.fileTemp)
                                    : attachment?.docId
                                      ? getFileUrl(attachment.docId)
                                      : undefined;
                                  return hasImage ? (
                                    <Image
                                      src={imageUrl}
                                      alt="preview"
                                      style={{ height: "100%", width: "100%", objectFit: "contain" }}
                                    />
                                  ) : (
                                    <Upload
                                      maxCount={1}
                                      accept="image/*"
                                      showUploadList={false}
                                      beforeUpload={(file) => {
                                        const isImage = file.type.startsWith("image/");
                                        if (!isImage) {
                                          notification.error({
                                            placement: 'top',
                                            message: t("product.onlyImageAllowed"),
                                          });
                                        }
                                        if (isImage) {
                                          form.setFieldValue(["attachments", name, "fileTemp"], file);
                                          form.setFieldValue(["attachments", name, "docId"], "temp");
                                        }
                                        return Upload.LIST_IGNORE;
                                      }}
                                    >
                                      <Button icon={<PlusOutlined />}>{t("product.uploadImage")}</Button>
                                    </Upload>
                                  );
                                }}
                              </Form.Item>
                            </div>
                            {/* 描述文本域 */}
                            <Form.Item name={[name, "description"]} style={{ marginBottom: 0 }}>
                              <Input.TextArea
                                autoSize={{ minRows: 2, maxRows: 4 }}
                                placeholder={t("product.descriptionPlaceholder")}
                                maxLength={500}
                              />
                            </Form.Item>
                            {/* 隐藏的表单字段用于验证 */}
                            <Form.Item
                              name={[name, "docId"]}
                              rules={[{ required: true, message: t("product.plUploadImage") }]}
                              style={{ display: "none" }}
                            >
                              <input />
                            </Form.Item>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </Form.List>
              </Form.Item>
            </Form>
          </div>
        </Spin>
      </Modal>
    </>
  );
};

export default AddOrEdit;
